/**
* @description: 设备利用率对比
*/
<template>
    <div class="finance_box" ref="myElementRef">
        <div ref="searchBox">
            <hc-search :data="searchData" @serch="onSearch">
                <template #btns>
                    <el-button type="success" icon="Download" @click="exportData">{{ t('deviceWorkTime.exportExcel') }}</el-button>
                </template>
            </hc-search>
        </div>
        <div class="tip-bar">
            {{ t('deviceWorkTime.utilizationTip') }}
        </div>

        <div class="table_box">
            <hc-table
                :loading="state.loading"
                :tableData="state.tableData"
                :column="state.tableHeader"
                :offsetHeight="state.offsetHeight"
                :maxPage="maxPage"
                @pageChange="pageChange"
                @limitChange="limitChange"
            >
                <template #rank="scope">
                    {{ (page - 1) * limit + scope.$index + 1 }}
                </template>
                <template #utilization_rate="{ row }">
                    <div class="rate-cell">
                        <el-progress
                            :percentage="Math.min(100, Number(row.utilization_rate) || 0)"
                            :stroke-width="10"
                            :format="() => `${row.utilization_rate ?? 0}%`"
                        />
                    </div>
                </template>
                <template #title="{ row }">
                    <el-button class="opBtn" size="small" type="primary" link @click="toDetail(row)">
                        {{ row.title }}
                    </el-button>
                </template>
                <template #operation="scope">
                    <el-button class="opBtn" size="small" type="primary" link @click="toDetail(scope.row, 'workTime')">
                        {{ t('deviceWorkTime.workTimeDetail') }}
                    </el-button>
                </template>
            </hc-table>
        </div>
    </div>
</template>

<script setup>
import hcSearch from "@/components/hc-search/index.vue";
import hcTable from "@/components/hc-table/index.vue";
import { onMounted, reactive, ref, nextTick, onUnmounted, toRaw } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { utilizationCompareApi, workSiteOptionsApi } from "@/api/system/device";
import { listToMap } from "@/utils/utils";
import { exportExcel } from "@/utils/apiBase";
import { usePageI18n } from "@/composables/usePageI18n.js";
import { applySearchLabels, applyColumnLabels, applyOptionLabels } from "@/utils/listPageI18n.js";

const myElementRef = ref();
const searchBox = ref();
const router = useRouter();

let page = ref(1);
let limit = ref(20);
let maxPage = ref(0);

const statusOptions = [
    { label: "", value: 99 },
    { label: "", value: 1 },
    { label: "", value: 0 },
];

const sortOptions = [
    { label: "", value: "desc" },
    { label: "", value: "asc" },
];

const onlyActiveOptions = [
    { label: "", value: 0 },
    { label: "", value: 1 },
];

const applyLabels = () => {
    applySearchLabels(searchData, {
        time: { labelKey: 'deviceWorkTime.month' },
        site: { labelKey: 'deviceWorkTime.site', placeholderKey: 'deviceWorkTime.allSites' },
        keywords: { labelKey: 'field.deviceName' },
        model: { labelKey: 'field.deviceCode' },
        status: { labelKey: 'field.status' },
        sort_order: { labelKey: 'deviceWorkTime.sort' },
        only_active: { labelKey: 'deviceWorkTime.scope' },
    });
    applyColumnLabels(state.column, {
        rank: 'deviceWorkTime.rank',
        code: 'field.deviceCode',
        title: 'field.deviceName',
        primary_site: 'deviceWorkTime.primarySite',
        model: 'field.model',
        utilization_rate: 'deviceWorkTime.utilizationRate',
        total_time: 'deviceWorkTime.totalTime',
        baseline_hours: 'deviceWorkTime.baselineHours',
        work_days: 'deviceWorkTime.workDays',
        operator_count: 'deviceWorkTime.operatorCount',
        operation: 'table.operation',
    });
    applyOptionLabels(statusOptions, {
        99: 'common.all',
        1: 'status.available',
        0: 'status.warehouse',
    });
    applyOptionLabels(sortOptions, {
        desc: 'deviceWorkTime.sortDesc',
        asc: 'deviceWorkTime.sortAsc',
    });
    applyOptionLabels(onlyActiveOptions, {
        0: 'deviceWorkTime.allDevices',
        1: 'deviceWorkTime.withHoursOnly',
    });
    const siteField = searchData.find((i) => i.field === 'site');
    if (siteField?.children?.[0]) {
        siteField.children[0].label = t('deviceWorkTime.allSites');
    }
};
const { t } = usePageI18n(applyLabels);

const searchData = reactive([
    { label: "月份", type: "month", value: "", field: "time" },
    {
        label: "工地",
        type: "select",
        value: "",
        field: "site",
        placeholder: "全部工地",
        children: [{ label: "全部工地", value: "" }],
        clearable: true,
    },
    { label: "设备名称", type: "input", value: "", placeholder: "", field: "keywords" },
    { label: "機號", type: "input", value: "", placeholder: "", field: "model" },
    {
        label: "状态",
        type: "select",
        children: statusOptions,
        value: 99,
        defaultValue: 99,
        field: "status",
        clearable: false,
    },
    {
        label: "排序",
        type: "select",
        children: sortOptions,
        value: "desc",
        defaultValue: "desc",
        field: "sort_order",
        clearable: false,
    },
    {
        label: "范围",
        type: "select",
        children: onlyActiveOptions,
        value: 0,
        defaultValue: 0,
        field: "only_active",
        clearable: false,
    },
]);

const state = reactive({
    loading: false,
    column: [
        { prop: "id", show: false, label: "ID" },
        { prop: "rank", show: true, label: "排名", width: 70 },
        { prop: "code", show: true, label: "機號", width: 130 },
        { prop: "title", show: true, label: "设备名称", width: 150 },
        { prop: "primary_site", show: true, label: "主要工地", width: 140 },
        { prop: "model", show: true, label: "机型", width: 110 },
        { prop: "utilization_rate", show: true, label: "利用率", width: 180 },
        { prop: "total_time", show: true, label: "合计工时(h)", width: 100 },
        { prop: "baseline_hours", show: true, label: "基准工时(h)", width: 100 },
        { prop: "work_days", show: true, label: "出勤天数", width: 90 },
        { prop: "operator_count", show: true, label: "机手人数", width: 90 },
        { prop: "operation", show: true, label: "操作", width: 100 },
    ],
    offsetHeight: 0,
    tableHeader: [],
    tableData: [],
});

const tableHeaderFn = () => {
    state.tableHeader = state.column.filter((v) => v.show);
};

const offHeighths = () => {
    if (myElementRef.value) {
        state.offsetHeight =
            myElementRef.value?.offsetHeight - 120 - searchBox.value?.offsetHeight;
    }
};

const buildParams = (pageNo, pageSize) => {
    const mapped = listToMap(searchData, { value: "value", key: "field" });
    const params = {
        page: pageNo,
        limit: pageSize,
        time: mapped.time ?? "",
        keywords: mapped.keywords ?? "",
        model: mapped.model ?? "",
        status: mapped.status ?? 99,
        sort_order: mapped.sort_order ?? "desc",
        only_active: mapped.only_active ?? 0,
    };
    if (mapped.site) {
        params.site = mapped.site;
    }
    return params;
};

const getMonthValue = () => {
    return toRaw(searchData.find((i) => i.field === "time") ?? {}).value ?? "";
};

const loadSiteOptions = async () => {
    const month = getMonthValue();
    const siteField = searchData.find((i) => i.field === "site");
    if (!month) {
        if (siteField) {
            siteField.children = [{ label: t('deviceWorkTime.allSites'), value: "" }];
        }
        return;
    }
    try {
        const res = await workSiteOptionsApi(JSON.stringify({ time: month }));
        const options = (res?.data ?? []).map((i) => ({
            label: i.site,
            value: i.site,
        }));
        if (siteField) {
            siteField.children = [{ label: t('deviceWorkTime.allSites'), value: "" }, ...options];
            const current = siteField.value;
            if (current && !options.some((o) => o.value === current)) {
                siteField.value = "";
            }
        }
    } catch {
        if (siteField) {
            siteField.children = [{ label: t('deviceWorkTime.allSites'), value: "" }];
        }
    }
};

const getSearch = () => {
    const params = buildParams(page.value, limit.value);
    if (!params.time) {
        ElMessage.warning(t('deviceWorkTime.selectMonth'));
        return Promise.resolve();
    }
    state.loading = true;
    return utilizationCompareApi(JSON.stringify(params))
        .then((res) => {
            maxPage.value = res?.maxPage ?? 0;
            state.tableData = res?.data ?? [];
        })
        .catch(() => {
            maxPage.value = 0;
            state.tableData = [];
        })
        .finally(() => {
            state.loading = false;
        });
};

const onSearch = async () => {
    page.value = 1;
    await loadSiteOptions();
    getSearch();
};

const pageChange = (currentPage) => {
    page.value = currentPage;
    getSearch();
};

const limitChange = (currentLimit) => {
    limit.value = currentLimit;
    page.value = 1;
    getSearch();
};

const toDetail = (row, tab = "info") => {
    router.push({
        path: "/equipment/device-detail",
        query: { id: row.id, tab },
    });
};

const exportData = async () => {
    const params = buildParams(1, 20);
    if (!params.time) {
        ElMessage.warning(t('deviceWorkTime.selectMonth'));
        return;
    }
    const exportParams = {
        time: params.time,
        sort_order: params.sort_order,
    };
    if (params.keywords) exportParams.keywords = params.keywords;
    if (params.model) exportParams.model = params.model;
    if (params.status != null && params.status < 99) exportParams.status = params.status;
    if (params.only_active === 1) exportParams.only_active = 1;
    if (params.site) exportParams.site = params.site;
    const siteSuffix = params.site ? `-${params.site}` : "";
    const { ok, msg } = await exportExcel(
        "Excel/deviceUtilizationExcel",
        exportParams,
        `设备利用率对比-${params.time}${siteSuffix}.xls`
    );
    if (!ok) {
        ElMessage.error(msg || t('deviceWorkTime.exportFailed'));
    }
};

onMounted(async () => {
    applyLabels();
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    searchData[0].value = `${now.getFullYear()}-${month}`;
    nextTick(() => offHeighths());
    window.onresize = () => nextTick(() => offHeighths());
    tableHeaderFn();
    await loadSiteOptions();
    getSearch();
});

onUnmounted(() => {
    window.onresize = null;
});
</script>

<style lang="less" scoped>
.finance_box {
    flex: 1;
    overflow: auto;
    background-color: #fff;
    border-radius: 6px;
    padding: 10px;
    margin-right: 10px;
}

.tip-bar {
    margin: 0 0 10px;
    padding: 8px 12px;
    font-size: 13px;
    color: #606266;
    background: #f4f8ff;
    border-radius: 4px;
    line-height: 1.5;
}

.table_box {
    width: 100%;
}

.rate-cell {
    padding-right: 8px;
}

.opBtn {
    margin: 0;
}
</style>
